import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import { sanitizeForLogging } from '@src/shared/utils/data-sanitizer';

class Observability {
  private isInitialized = false;

  canNot() {
    const env = import.meta.env.VITE_NODE_ENV;
    return env !== 'homologation' && env !== 'prod';
  }

  start() {
    if (this.canNot()) {
      return;
    }

    try {
      Bugsnag.start({
        apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
        appVersion: import.meta.env.VITE_APP_VERSION,
        plugins: [new BugsnagPluginReact()],
        enabledReleaseStages: ['homologation', 'prod'],
        releaseStage: import.meta.env.VITE_NODE_ENV,
        onError: (event) => {
          event.addMetadata('boleto-parcelado-pagol', {
            name: 'Boleto Parcelado Pagol',
            environment: import.meta.env.VITE_NODE_ENV,
          });
          return true;
        },
      });

      BugsnagPerformance.start({
        apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
        appVersion: import.meta.env.VITE_APP_VERSION,
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('[Observability] Failed to start Bugsnag:', error);
    }
  }

  navigation = (uri: string) => {
    if (this.canNot() || !this.isInitialized) return;

    try {
      Bugsnag.leaveBreadcrumb('PAGE: ' + uri);
    } catch (error) {
      console.error('[Bugsnag] Failed to leave breadcrumb:', error);
    }
  };

  error = (error: Error) => {
    if (this.canNot() || !this.isInitialized) {
      return;
    }

    try {
      Bugsnag.notify(error, (event) => {
        event.addMetadata('boleto-parcelado-pagol', {
          name: 'Boleto Parcelado Pagol',
          environment: import.meta.env.VITE_NODE_ENV,
        });

        // Sanitize error data before adding metadata
        const sanitizedError = sanitizeForLogging({
          message: error.message,
          stack: error.stack,
          ...(error as any), // Include any additional error properties
        });

        event.addMetadata('HTTP_ERROR', sanitizedError);
      });
    } catch (err) {
      console.error('[Bugsnag] Failed to notify error:', err);
    }
  };

  setUser = (proposalId: string, email?: string, name?: string) => {
    if (this.canNot() || !this.isInitialized) {
      return;
    }

    try {
      // Sanitize user data before sending to Bugsnag
      const sanitizedData = sanitizeForLogging({
        email,
        name,
      });

      // Set user with proposalId as ID
      Bugsnag.setUser(proposalId, sanitizedData.email, sanitizedData.name);

      // Add proposalId to metadata for better visibility
      Bugsnag.addMetadata('user', {
        proposalId: proposalId,
      });
    } catch (error) {
      console.error('[Bugsnag] Failed to set user:', error);
    }
  };

  logApiCall = (data: {
    method: string;
    url: string;
    status: number;
    duration?: number;
    requestData?: any;
    responseData?: any;
  }) => {
    if (this.canNot() || !this.isInitialized) return;

    try {
      const { method, url, status, duration, requestData, responseData } = data;
      const isError = status >= 400;

      // Sanitize sensitive data before logging
      const sanitizedRequest = requestData
        ? sanitizeForLogging(requestData)
        : undefined;
      const sanitizedResponse = responseData
        ? sanitizeForLogging(responseData)
        : undefined;

      Bugsnag.leaveBreadcrumb(
        `API ${method.toUpperCase()} ${status}`,
        {
          type: 'request',
          url,
          method: method.toUpperCase(),
          status,
          duration: duration ? `${duration}ms` : undefined,
          requestData: sanitizedRequest
            ? JSON.stringify(sanitizedRequest)
            : undefined,
          responseData: sanitizedResponse
            ? JSON.stringify(sanitizedResponse)
            : undefined,
        },
        isError ? 'error' : 'request'
      );
    } catch (error) {
      console.error('[Bugsnag] Failed to log API call:', error);
    }
  };
}

export const observability = new Observability();
